import {
  deleteApi, getApi, handleError, postApi, putApi,
} from '@services/agent-foundation/agent-foundation-api-service';
import type {
  BoomiModel,
  ModelConfig,
  RegisterModelRequest,
  // UpdateModelRequest,
} from '@components/ModelsManagement/types/models.types';
import type { RegisterModelFormData } from '@components/ModelsManagement/CreateModelConfigurationDrawer/types';

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  timestamp: number;
}

/** Masked view of the account's hidden Jev credential config. */
export interface SystemOneJevConfig {
  id?: string | null;
  configured: boolean;
  api_key_hint?: string | null;
  base_url?: string | null;
  model_id?: string | null;
  created_on?: string | null;
  created_by?: string | null;
  last_updated_on?: string | null;
  last_updated_by?: string | null;
}

export interface ModelConfigDetailsResponse {
  id: string;
  name: string;
  description: string;
  provider: string;
  environments: Array<{
    environment_id: string;
    environment_name: string;
    classification: string;
    runtimes: Array<{
      runtime_id: string;
      runtime_name: string;
      status: string;
    }>;
    deployment_count: number;
    removable: boolean;
    created_on: string;
  }>;
  deployment_count: number;
  models: Array<{
    model_id: string;
    provider: string;
    display_name: string;
    description: string;
    context_window: number;
    max_output_tokens: number;
    capabilities: Record<string, boolean>;
    tier: number;
    params: Record<string, any>;
    status: string;
    scope: string;
    deployment_count: number;
    removable: boolean;
    created_on: string;
  }>;
  created_by: string;
  created_on: string;
  last_updated_by: string;
  last_updated_on: string;
}

export type ModelProvider = 'Anthropic' | 'OpenAI' | 'Gemini' | 'Custom' | 'anthropic' | 'gemini' | 'openai' | 'google' | 'Google' | 'Typesafe' | 'typesafe' | 'any';

const LATENCY_MS = 400;

const delay = <T>(value: T): Promise<T> => new Promise((resolve) => {
  setTimeout(() => resolve(value), LATENCY_MS);
});

const modelsApiService = {
  async getModelConfigs(
    filters?: Record<string, any>,
  ): Promise<{ items: ModelConfig[]; total_count: number }> {
    try {
      const apiUrl = '/agent-garden/model-configurations';
      const queryString = filters
        ? `?${Object.entries(filters)
          .flatMap(([key, value]) => {
            if (value === undefined || value === null) {
              return [];
            }
            if (Array.isArray(value)) {
              return value.map(
                (v) => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`,
              );
            }
            return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`];
          })
          .join('&')}`
        : '';

      const response: any = await getApi(`${apiUrl}${queryString}`);
      if (response?.success && response?.data) {
        const items = Array.isArray(response.data)
          ? response.data
          : response.data.items || [];
        const totalCount = response.data?.total_items || items.length;
        return { items, total_count: totalCount };
      }
      handleError(response, 'Failed to load model configurations');
      return { items: [], total_count: 0 };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load models:', error);
      throw error;
    }
  },

  async getBoomiModels(): Promise<BoomiModel[]> {
    const apiUrl = '/agent-garden/models/managed';
    const response: any = await getApi(apiUrl);
    if (!response?.success) {
      handleError(response, 'Failed to load registered models');
      return [];
    }
    const items = response?.data?.items || response?.data || [];
    return items;
  },

  async getModelCatalog(queryParams?: Record<string, any>): Promise<any[]> {
    try {
      const apiUrl = '/agent-garden/models/catalog';
      const queryString = queryParams
        ? `?${
          Object.entries(queryParams)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&')}`
        : '';
      const response: any = await getApi(`${apiUrl}${queryString}`);
      if (!response?.success) {
        handleError(response, 'Failed to load model catalog');
        return [];
      }
      const items = response?.data?.items || response?.data || [];
      return items;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch model catalog:', error);
      handleError(error, 'Failed to load model catalog');
      return [];
    }
  },

  async getModelsList(id: string): Promise<any> {
    const apiUrl = `/agent-garden/model-configurations/${id}`;
    const response = await getApi(apiUrl);
    return response;
  },

  async registerModelConfiguration(
    formDataOrReq: RegisterModelFormData | RegisterModelRequest,
  ): Promise<any> {
    // Handle both RegisterModelFormData and RegisterModelRequest
    const formData = formDataOrReq as RegisterModelFormData;

    try {
      // API request payload
      const selectedEnvs = Array.isArray(formData.environment)
        ? formData.environment
        : [formData.environment];

      const apiPayload = {
        name: formData.modelName,
        provider: (formData.provider as string).toLowerCase(),
        api_key: formData.apiKey,
        environment_ids: selectedEnvs,
        model_ids: formData.models,
      };

      // Call actual API endpoint POST /model-configurations
      // const response = await getAPI('model-configurations', 'POST', apiPayload);
      const apiUrl = '/agent-garden/model-configurations';
      const response = await postApi(apiUrl, apiPayload);
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to register model configuration:', error);
      throw error;
    }
  },

  async updateModelConfiguration(
    id: string,
    formData: RegisterModelFormData,
  ): Promise<any> {
    try {
      const apiPayload: any = {
        name: formData.modelName,
        environment_ids: formData.environment,
        model_ids: formData.models,
      };

      if (formData.apiKey?.trim()) {
        apiPayload.api_key = formData.apiKey;
      }

      const apiUrl = `/agent-garden/model-configurations/${id}`;
      const response = await putApi(apiUrl, apiPayload);
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update model configuration:', error);
      throw error;
    }
  },
  async checkConfigurationNameExists(configName: string): Promise<boolean> {
    // Check if a configuration with this name already exists
    const response = await this.getModelConfigs();
    const exists = response.items.some(
      (model) => (model.display_name || model.name || '')
        .toLowerCase() === configName.toLowerCase(),
    );
    return delay(exists);
  },

  async getRegisteredModels(queryParams?: Record<string, any>): Promise<any | null> {
    const queryString = queryParams
      ? `?${Object.entries(queryParams)
        .flatMap(([key, value]) => {
          if (value === undefined || value === null) {
            return [];
          }
          if (Array.isArray(value)) {
            return value.map(
              (v) => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`,
            );
          }
          return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`];
        })
        .join('&')}`
      : '';
    const apiUrl = '/agent-garden/models/configured';
    const response: any = await getApi(`${apiUrl}${queryString}`);
    return response;
  },

  async getModelConfigDetails(
    configId: string,
  ): Promise<any | null> {
    const apiUrl = `/agent-garden/model-configurations/${configId}`;
    const response = await getApi(apiUrl);
    return response;
  },

  async deleteModelConfiguration(
    configId: string,
  ): Promise<any> {
    try {
      if (!configId) {
        throw new Error('Model ID is required to delete a configuration');
      }
      const apiUrl = `/agent-garden/model-configurations/${configId}`;
      const response = await deleteApi(apiUrl);
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete model configuration:', error);
      throw error;
    }
  },

  async getConfigurationsByProviderAndModel(
    provider: string,
    modelId: string,
  ): Promise<ModelConfig[]> {
    try {
      const apiUrl = '/agent-garden/model-configurations';
      const queryString = `?provider=${encodeURIComponent(provider)}&model_id=${encodeURIComponent(modelId)}`;
      const response: any = await getApi(`${apiUrl}${queryString}`);
      if (response?.success && response?.data) {
        return Array.isArray(response.data) ? response.data : response.data.items || [];
      }
      return [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load configurations:', error);
      throw error;
    }
  },

  async getSystemOneJevConfig(agentId: string): Promise<SystemOneJevConfig | null> {
    const apiUrl = `/agent-garden/tools/system-one-jev/${encodeURIComponent(agentId)}`;
    const response: any = await getApi(apiUrl);
    if (!response?.success) {
      handleError(response, 'Failed to load the Jev API key status');
      return null;
    }
    return response?.data || null;
  },

  async saveSystemOneJevKey(agentId: string, apiKey: string): Promise<any> {
    const apiUrl = `/agent-garden/tools/system-one-jev/${encodeURIComponent(agentId)}`;
    return postApi(apiUrl, { api_key: apiKey });
  },
};

export default modelsApiService;
